import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import { toast } from "react-toastify";
import {
  renewMembership,
  renewMembershipSuccess,
} from "../../../store/actions/MemberAction/memberActions";
import { openMembershipCheckout } from "../../../utils/membershipPay";

// Member portal - the logged-in member's own membership status.
// Shows Name, Registration Number, Valid Till, Centre, Course and Status,
// the fee slip, and (after expiry) a Renew option.
export default function MyStatus() {
  const dispatch = useDispatch();
  const { member } = useSelector((state) => state);
  const profile = member?.memberAuth?.profile || {};
  const member_type = localStorage.getItem("member_type");
  const isIndividual = member_type === "Individual";
  const kind = isIndividual ? "individual" : "other";
  const [renewing, setRenewing] = useState(false);

  const name = isIndividual ? profile?.student_name : profile?.name;
  const approved = Number(profile?.is_approwed) === 1;
  const rejected = !approved && profile?.reject_reason;
  const paid = Number(profile?.payment_status) === 1;

  // Lifetime never expires; 1-Year expires on valid_until.
  const lifetime = profile?.membership_plan === "lifetime";
  const validUntil = profile?.valid_until ? moment(profile.valid_until) : null;
  const expired =
    !lifetime && validUntil ? validUntil.isBefore(moment(), "day") : false;

  const statusText = approved
    ? "Approved"
    : rejected
    ? "Rejected"
    : "Pending";
  const statusColor = approved ? "green" : rejected ? "red" : "#b8860b";

  const slipUrl = `https://nsvec.com/api/public/certificate/membership-receipt/${kind}/${btoa(
    String(profile?.id)
  )}`;

  const handleRenew = async () => {
    setRenewing(true);
    try {
      const res = await dispatch(
        renewMembership({ member_id: profile?.id, member_type: kind })
      );
      const pay = res?.pay;
      if (res?.status !== "success" || !pay) {
        toast.error(res?.message || "Could not start renewal. Try again.");
        setRenewing(false);
        return;
      }
      if (pay.free) {
        toast.success("Membership renewed.");
        setTimeout(() => window.location.reload(), 1200);
        return;
      }
      if (pay.pay_error || !pay.order_id) {
        toast.error(pay.pay_error || "Could not start the payment. Try again.");
        setRenewing(false);
        return;
      }
      const result = await openMembershipCheckout(pay);
      if (result.payment_id) {
        const done = await dispatch(
          renewMembershipSuccess({
            member_id: profile?.id,
            member_type: kind,
            razorpay_payment_id: result.payment_id,
          })
        );
        if (done?.status === "success") {
          toast.success("Membership renewed successfully.");
          setTimeout(() => window.location.reload(), 1200);
          return;
        }
        toast.error(done?.message || "Renewal could not be verified.");
      } else if (result.dismissed) {
        toast.error("Payment cancelled. Membership was not renewed.");
      } else if (result.error) {
        toast.error(result.error);
      }
      setRenewing(false);
    } catch (e) {
      toast.error("Could not start renewal. Try again.");
      setRenewing(false);
    }
  };

  const Row = ({ label, value }) => (
    <>
      <div className="col-lg-4 table-box">{label}</div>
      <div className="col-lg-8 table-box">{value || "-"}</div>
    </>
  );

  return (
    <div>
      <div className="gallery-box">
        <div className="container">
          <div className="title-box">
            <div className="col-md-12 inner">
              <div className="row">
                <div className="col-lg-12">
                  <h3>My Membership Status</h3>
                </div>

                <Row label="Name" value={name} />
                <Row
                  label="Registration Number"
                  value={profile?.registration_number}
                />
                {kind === "other" && profile?.unique_registration_number ? (
                  <Row
                    label="Unique Registration No."
                    value={profile.unique_registration_number}
                  />
                ) : null}
                <Row
                  label="Membership Plan"
                  value={
                    lifetime
                      ? "Lifetime"
                      : profile?.membership_plan === "1y"
                      ? "1 Year"
                      : "-"
                  }
                />
                <Row
                  label="Valid Till"
                  value={
                    lifetime
                      ? "Lifetime (no expiry)"
                      : validUntil
                      ? validUntil.format("DD/MM/YYYY")
                      : "-"
                  }
                />
                {isIndividual ? (
                  <Row
                    label="Course"
                    value={profile?.course_name || profile?.course}
                  />
                ) : null}
                <Row
                  label="Centre Name"
                  value={
                    isIndividual
                      ? profile?.center_name
                      : profile?.location || profile?.city
                  }
                />

                <div className="col-lg-4 table-box">Payment</div>
                <div className="col-lg-8 table-box">
                  <span style={{ color: paid ? "green" : "#b8860b", fontWeight: "bold" }}>
                    {paid ? "Paid" : "Pending"}
                  </span>
                </div>

                <div className="col-lg-4 table-box">Status</div>
                <div className="col-lg-8 table-box">
                  <span style={{ color: statusColor, fontWeight: "bold" }}>
                    {statusText}
                  </span>
                </div>

                {rejected ? (
                  <>
                    <div className="col-lg-4 table-box">Reject Reason</div>
                    <div className="col-lg-8 table-box" style={{ color: "red" }}>
                      {profile.reject_reason}
                    </div>
                  </>
                ) : null}

                {paid ? (
                  <>
                    <div className="col-lg-4 table-box">Fee Slip</div>
                    <div className="col-lg-8 table-box">
                      <a href={slipUrl} target="_blank" rel="noopener noreferrer">
                        View / Download Fee Slip
                      </a>
                    </div>
                  </>
                ) : null}

                {expired ? (
                  <>
                    <div className="col-lg-4 table-box" style={{ color: "red" }}>
                      Membership Expired
                    </div>
                    <div className="col-lg-8 table-box">
                      <button
                        className="btn btn-sm btn-danger"
                        disabled={renewing}
                        onClick={handleRenew}
                      >
                        {renewing ? "Please wait..." : "Renew Membership"}
                      </button>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
